/**
 * TanStack Query hooks over the typed API, kept fresh by hub frames: `run` frames patch the
 * runs list, `inbox` / `inbox.ranked` frames replace the inbox, and `work` frames invalidate the
 * work-model queries they touch. No polling while the socket is live.
 */
import { type QueryClient, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useSyncExternalStore } from 'react'
import { type Backend, useBackend } from './backend.js'
import type { ConnState } from './hub.js'
import type { InboxItem, RankedInbox, RunSummary, WorkFrame } from './types.js'

export const qk = {
  session: ['session'] as const,
  runs: ['runs'] as const,
  inbox: ['inbox'] as const,
  ranked: ['inbox', 'ranked'] as const,
  machines: ['machines'] as const,
  projects: ['projects'] as const,
  devices: ['devices'] as const,
  credentials: ['credentials'] as const,
  usage: ['usage'] as const,
  hygiene: ['hygiene'] as const,
  settings: ['settings'] as const,
  ideas: ['ideas'] as const,
  grants: ['grants'] as const,
  metrics: (days: number) => ['metrics', days] as const,
  workstreams: (projectId?: string) => ['workstreams', projectId ?? '*'] as const,
  workstream: (id: string) => ['workstream', id] as const,
  tasks: (q: { projectId?: string; workstreamId?: string }) =>
    ['tasks', q.projectId ?? '*', q.workstreamId ?? '*'] as const,
  task: (id: string) => ['task', id] as const,
  rules: (projectId?: string) => ['rules', projectId ?? '*'] as const,
  landings: (projectId?: string) => ['landings', projectId ?? '*'] as const,
  builds: (projectId?: string) => ['builds', projectId ?? '*'] as const,
  artifacts: (q: Record<string, string | undefined>) => ['artifacts', q] as const,
  sched: ['sched', 'machines'] as const,
  card: (id: string) => ['card', id] as const,
}

export const sessionQuery = (b: Backend) => ({
  queryKey: qk.session,
  queryFn: () => b.api.session(),
  staleTime: 60_000,
})

export function useSession() {
  const b = useBackend()
  return useQuery(sessionQuery(b))
}

/** Which cached queries a `work` frame makes stale (prefix keys). */
export function workKeys(f: WorkFrame): (readonly unknown[])[] {
  switch (f.entity) {
    case 'project':
      return [qk.projects]
    case 'workstream':
      return [qk.projects, ['workstreams'], qk.workstream(f.id)]
    case 'task':
      return [qk.projects, ['tasks'], qk.task(f.id), ['workstream']]
    case 'idea':
      return [qk.ideas]
    case 'rule':
      return [['rules']]
    case 'landing':
      return [['landings'], qk.runs, qk.projects]
    case 'build':
      return [['builds'], qk.runs, qk.projects]
    case 'artifact':
      return [['artifacts']]
    case 'grant':
      return [qk.grants]
    case 'card':
      return [qk.card(f.id)]
    // `['machines', …]` also covers machine load and machine projects (report projections).
    case 'machine':
      return [qk.machines, qk.sched]
    default:
      return []
  }
}

/** Which cached queries a `work` frame makes stale (immediate; legacy). */
export function invalidateForWork(qc: QueryClient, f: WorkFrame) {
  for (const queryKey of workKeys(f)) void qc.invalidateQueries({ queryKey })
}

export const WORK_INVALIDATE_WINDOW_MS = 2_000
export type WorkInvalidator = ((f: WorkFrame) => void) & { dispose(): void }

/**
 * Throttled `work`-frame invalidation (burst-guard). Per query key: invalidate on the leading edge
 * when idle, then at most one trailing invalidation per window, always with
 * `cancelRefetch: false` so an in-flight refetch is never restarted. While the tab is hidden,
 * invalidations wait and run once when it becomes visible again (hidden tabs send no HTTP).
 *
 * `{ windowMs: 0, cancelRefetch: true }` is the legacy behaviour exactly: every frame invalidates
 * every key immediately (restarting in-flight refetches), hidden or not.
 */
export function createWorkInvalidator(
  qc: QueryClient,
  o: {
    windowMs?: number
    cancelRefetch?: boolean
    /** Defaults to `document.visibilityState === 'hidden'` (never hidden without a DOM). */
    hidden?: () => boolean
    now?: () => number
  } = {},
): WorkInvalidator {
  const windowMs = o.windowMs ?? WORK_INVALIDATE_WINDOW_MS
  const cancelRefetch = o.cancelRefetch ?? false
  if (windowMs <= 0 && cancelRefetch) {
    const legacy = (f: WorkFrame) => invalidateForWork(qc, f)
    return Object.assign(legacy, { dispose() {} })
  }
  const now = o.now ?? Date.now
  const hidden =
    o.hidden ?? (() => typeof document !== 'undefined' && document.visibilityState === 'hidden')
  const keys = new Map<
    string,
    { key: readonly unknown[]; last: number; timer?: ReturnType<typeof setTimeout> }
  >()
  const deferred = new Map<string, readonly unknown[]>()
  let disposed = false
  const invalidate = (key: readonly unknown[]) =>
    void qc.invalidateQueries({ queryKey: key }, { cancelRefetch })
  const fire = (id: string, key: readonly unknown[]) => {
    if (hidden()) {
      deferred.set(id, key)
      return
    }
    let s = keys.get(id)
    if (!s) {
      s = { key, last: Number.NEGATIVE_INFINITY }
      keys.set(id, s)
    }
    if (s.timer) return // one trailing invalidation already covers this frame
    const t = now()
    const wait = s.last + windowMs - t
    if (wait <= 0) {
      s.last = t
      invalidate(key)
      return
    }
    const state = s
    state.timer = setTimeout(() => {
      state.timer = undefined
      if (disposed) return
      if (hidden()) {
        deferred.set(id, key)
        return
      }
      state.last = now()
      invalidate(key)
    }, wait)
  }
  const onVisible = () => {
    if (hidden() || !deferred.size) return
    const pending = [...deferred]
    deferred.clear()
    for (const [id, key] of pending) fire(id, key)
  }
  if (typeof document !== 'undefined') document.addEventListener('visibilitychange', onVisible)
  const run = (f: WorkFrame) => {
    if (disposed) return
    for (const key of workKeys(f)) fire(JSON.stringify(key), key)
  }
  return Object.assign(run, {
    dispose() {
      disposed = true
      for (const s of keys.values()) if (s.timer) clearTimeout(s.timer)
      keys.clear()
      deferred.clear()
      if (typeof document !== 'undefined')
        document.removeEventListener('visibilitychange', onVisible)
    },
  })
}

/** Wires hub frames into the query cache. Mount once inside the authenticated app. */
export function useHubSync() {
  const b = useBackend()
  const qc = useQueryClient()
  useEffect(() => {
    b.hub.connect()
    const offRun = b.hub.onRun((run) => {
      if (b.content) return // Relay plaintext cannot replace authenticated endpoint routing.
      qc.setQueryData<RunSummary[]>(qk.runs, (prev) => {
        if (!prev) return prev
        const i = prev.findIndex((r) => r.runId === run.runId)
        return i === -1
          ? [run, ...prev]
          : prev.map((r) => (r.runId === run.runId ? { ...r, ...run } : r))
      })
    })
    const offInbox = b.hub.onInbox((items) => qc.setQueryData<InboxItem[]>(qk.inbox, items))
    const offRanked = b.hub.onRanked((r) => qc.setQueryData<RankedInbox>(qk.ranked, r))
    const invalidate = createWorkInvalidator(qc)
    const offWork = b.hub.onWork(invalidate)
    const online = () => b.hub.wake()
    const offline = () => b.hub.goOffline()
    const visible = () => {
      if (document.visibilityState === 'visible') b.hub.wake()
    }
    addEventListener('online', online)
    addEventListener('offline', offline)
    document.addEventListener('visibilitychange', visible)
    return () => {
      offRun()
      offInbox()
      offRanked()
      offWork()
      invalidate.dispose()
      removeEventListener('online', online)
      removeEventListener('offline', offline)
      document.removeEventListener('visibilitychange', visible)
    }
  }, [b, qc])
}

export function useConnection(): ConnState {
  const b = useBackend()
  return useSyncExternalStore(
    (cb) => b.hub?.onState(cb) ?? (() => {}),
    () => b.hub?.connection ?? 'idle',
  )
}

/**
 * Poll only while the socket is down; frames keep the cache fresh otherwise. (Ticks also skip
 * whenever the tab is hidden or unfocused: query-client.ts.)
 */
export function useFallbackPoll(ms = 30_000) {
  return useConnection() === 'live' ? false : ms
}

export function useRuns() {
  const b = useBackend()
  const refetchInterval = useFallbackPoll()
  const revision = useSyncExternalStore(
    b.subscribeRuns ?? (() => () => {}),
    b.runsVersion ?? (() => 0),
  )
  const accountId = b.artifactAccess?.accountId ?? ''
  const query = useQuery({
    queryKey: b.content ? [...qk.runs, accountId] : qk.runs,
    queryFn: () => b.api.runs(),
    refetchInterval,
  })
  const refetch = query.refetch
  useEffect(() => {
    if (b.content && revision) void refetch()
  }, [b, revision, refetch])
  return query
}

export function useRun(runId: string | undefined) {
  const runs = useRuns()
  return runs.data?.find((r) => r.runId === runId)
}

export function useInbox() {
  const b = useBackend()
  const refetchInterval = useFallbackPoll()
  return useQuery({ queryKey: qk.inbox, queryFn: () => b.api.inbox(), refetchInterval })
}

export function useInboxRanked() {
  const b = useBackend()
  const refetchInterval = useFallbackPoll(60_000)
  return useQuery({
    queryKey: qk.ranked,
    queryFn: () => b.api.inboxRanked(),
    refetchInterval,
    retry: false,
  })
}

/** Machine rows follow the hub's `work{machine}` pushes; poll only while the socket is down. */
export function useMachines() {
  const b = useBackend()
  const refetchInterval = useFallbackPoll(60_000)
  return useQuery({ queryKey: qk.machines, queryFn: () => b.api.machines(), refetchInterval })
}

export function useProjects() {
  const b = useBackend()
  return useQuery({
    queryKey: b.content ? [...qk.projects, b.artifactAccess.accountId] : qk.projects,
    queryFn: () => b.api.projects(),
    retry: false,
  })
}

export function useProject(id: string | undefined) {
  const p = useProjects()
  return { ...p, data: p.data?.find((x) => x.id === id) }
}

export function useWorkstreams(projectId?: string) {
  const b = useBackend()
  return useQuery({
    queryKey: b.content
      ? [...qk.workstreams(projectId), b.artifactAccess.accountId]
      : qk.workstreams(projectId),
    queryFn: () => b.api.workstreams(projectId ? { projectId } : {}),
    retry: false,
  })
}

export function useWorkstream(id: string) {
  const b = useBackend()
  return useQuery({
    queryKey: b.content ? [...qk.workstream(id), b.artifactAccess.accountId] : qk.workstream(id),
    queryFn: () => b.api.workstream(id),
  })
}

export function useTasks(q: { projectId?: string; workstreamId?: string }) {
  const b = useBackend()
  return useQuery({
    queryKey: b.content ? [...qk.tasks(q), b.artifactAccess.accountId] : qk.tasks(q),
    queryFn: () => b.api.tasks(q),
    enabled: !!(q.projectId || q.workstreamId),
  })
}

export function useTask(id: string) {
  const b = useBackend()
  return useQuery({
    queryKey: b.content ? [...qk.task(id), b.artifactAccess.accountId] : qk.task(id),
    queryFn: () => b.api.task(id),
  })
}

export function useRules(projectId?: string) {
  const b = useBackend()
  return useQuery({
    queryKey: b.content
      ? [...qk.rules(projectId), b.artifactAccess.accountId]
      : qk.rules(projectId),
    queryFn: () => b.api.rules(projectId ? { projectId } : {}),
    retry: false,
  })
}

export function useLandings(projectId?: string) {
  const b = useBackend()
  return useQuery({
    queryKey: qk.landings(projectId),
    queryFn: () => b.api.landings(projectId ? { projectId } : {}),
    retry: false,
  })
}

export function useBuilds(projectId?: string) {
  const b = useBackend()
  return useQuery({
    queryKey: qk.builds(projectId),
    queryFn: () => b.api.builds(projectId ? { projectId } : {}),
    retry: false,
  })
}

export function useArtifacts(q: { projectId?: string; taskId?: string; runId?: string }) {
  const b = useBackend()
  return useQuery({
    queryKey: qk.artifacts(q),
    queryFn: () => b.api.artifacts(q),
    retry: false,
  })
}

export function useIdeas() {
  const b = useBackend()
  return useQuery({
    queryKey: b.content ? [...qk.ideas, b.artifactAccess.accountId] : qk.ideas,
    queryFn: () => b.api.ideas(),
    retry: false,
  })
}

export function useGrants() {
  const b = useBackend()
  return useQuery({ queryKey: qk.grants, queryFn: () => b.api.grants(), retry: false })
}

export function useDevices() {
  const b = useBackend()
  return useQuery({ queryKey: qk.devices, queryFn: () => b.api.devices() })
}

export function useCredentials() {
  const b = useBackend()
  return useQuery({ queryKey: qk.credentials, queryFn: () => b.api.credentials() })
}

export function useUsage() {
  const b = useBackend()
  return useQuery({ queryKey: qk.usage, queryFn: () => b.api.usage(), retry: false })
}

export function useSchedMachines() {
  const b = useBackend()
  return useQuery({ queryKey: qk.sched, queryFn: () => b.api.schedMachines(), retry: false })
}

export function useMetrics(days = 7) {
  const b = useBackend()
  return useQuery({
    queryKey: qk.metrics(days),
    queryFn: () => b.api.metrics(days),
    retry: false,
    staleTime: 5 * 60_000,
  })
}

export function useHygiene() {
  const b = useBackend()
  return useQuery({ queryKey: qk.hygiene, queryFn: () => b.api.hygiene(), retry: false })
}

export function useSettings() {
  const b = useBackend()
  return useQuery({ queryKey: qk.settings, queryFn: () => b.api.settings(), retry: false })
}

export function isLive(r: RunSummary) {
  return r.status === 'running' || r.status === 'waiting' || r.status === 'compacting'
}
